'use client'

import type { CarouselProps } from '@pearlconvert/ui/components/carousel/carousel.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import React, { Children } from 'react'

import { useCarousel } from '@pearlconvert/ui/components/carousel/batons/use-carousel'
import CarouselSlide from '@pearlconvert/ui/components/carousel/carousel-slide'
import CarouselIndicator from '@pearlconvert/ui/components/carousel/scrolling-indicator'

import '@pearlconvert/ui/components/carousel/styles/carousel.scss'

export default function Carousel(props: CarouselProps) {
  const {
    children,
    id,
    name,
    slideName,
    slideIndex: currentSlideIndex,
    snapToGrid = false,
    onSlideChange,
    showNavigationArrows = true,
    noWrap = false,
    recommendationProps: { trackNavClick } = {},
  } = props
  const t = useTranslations('ui')
  const slideCount = Children.count(children)
  const {
    carouselWrapperRef,
    isLoading,
    viewCount,
    viewIndex,
    updateViewIndex,
    slidesRef,
    slidesContainerRef,
    slidesPerView,
  } = useCarousel({
    slideCount,
    currentSlideIndex,
    snapToGrid,
    onSlideChange,
    trackRecommendationNavClick: trackNavClick,
  })

  const mappedSlides = Children.map(children, (child, index) => {
    if (noWrap) {
      return child
    }
    return (
      <CarouselSlide
        key={index}
        name={slideName}
        slidesRef={slidesRef}
        index={index}
      >
        {child}
      </CarouselSlide>
    )
  })

  return (
    <div
      id={id}
      className={classNames('carousel', {
        'carousel--single-image': slideCount === 1,
        'carousel--loading': isLoading,
      })}
      role="region"
      aria-roledescription="carousel"
      aria-label={name}
    >
      <CarouselIndicator
        carouselRef={slidesContainerRef}
        slideCount={viewCount}
        slideIndex={viewIndex}
        goToSlide={updateViewIndex}
        isLoading={isLoading}
        className={classNames('carousel__navigation', {
          'carousel__navigation--show': !isLoading && viewCount > 1,
        })}
        caruselItems={slideCount}
        slidesPerView={slidesPerView}
        noWrap={noWrap}
        carouselName={name}
      />
      <div className="carousel__wrapper" ref={carouselWrapperRef}>
        {showNavigationArrows && slideCount > slidesPerView && !isLoading && (
          <button
            className={classNames(
              'carousel__button',
              'carousel__button--prev',
              {
                'carousel__button--disabled': viewIndex === 0,
              }
            )}
            aria-label={t('carousel.scroll-left')}
            onClick={() => updateViewIndex(viewIndex - 1)}
            disabled={viewIndex === 0}
          />
        )}
        <div className="carousel__container">
          <ul
            ref={slidesContainerRef}
            className={classNames('carousel__slides', {
              'snap-to-grid': snapToGrid,
            })}
          >
            {mappedSlides}
          </ul>
        </div>
        {showNavigationArrows && slideCount > slidesPerView && !isLoading && (
          <button
            className={classNames(
              'carousel__button',
              'carousel__button--next',
              {
                'carousel__button--disabled': viewCount - viewIndex === 1,
              }
            )}
            aria-label={t('carousel.scroll-right')}
            onClick={() => updateViewIndex(viewIndex + 1)}
            disabled={viewCount - viewIndex === 1}
          />
        )}
      </div>
    </div>
  )
}
